// =============================================================================
// Origin UI — src/pages/compliance/admin/ComplianceContentAdmin.tsx
// CM-12 — content management: items & versions (Compliance role only).
//   - List items (attestations + declarations).
//   - Create / edit an item (type, code, title, Synthesia video id, gating steps).
//   - Publish a new version: paste the form schema JSON + optional PDF upload.
//     (CM-13 replaces the raw-JSON schema box with a no-code field builder.)
//   - Assign-now (attestations) — pushes the current version to all active staff.
// Backend endpoints are from CM-4. Self-guards on the Compliance role.
// =============================================================================

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { complianceApi } from '@/services/complianceApi';
import type {
  ComplianceItemAdminListItem,
  ComplianceItemType,
  ComplianceGatingStep,
} from '@/services/complianceApi';
import { FormFieldBuilder } from '@/components/compliance/FormFieldBuilder';
import styles from '../compliance.module.css';

type Mode = 'list' | 'create' | 'edit';

const DEFAULT_GATING: ComplianceGatingStep[] = [
  { step: 'Video', enabled: false, order: 1 },
  { step: 'Document', enabled: false, order: 2 },
  { step: 'Form', enabled: true, order: 3 },
];

export function ComplianceContentAdmin() {
  const { roles, rolesLoaded } = useAuth();
  const navigate = useNavigate();

  const [items, setItems] = useState<ComplianceItemAdminListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [mode, setMode] = useState<Mode>('list');
  const [editingId, setEditingId] = useState<string | null>(null);

  // ── Item form fields ──
  const [code, setCode] = useState('');
  const [title, setTitle] = useState('');
  const [itemType, setItemType] = useState<ComplianceItemType>('Attestation');
  const [videoId, setVideoId] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [gating, setGating] = useState<ComplianceGatingStep[]>(DEFAULT_GATING);
  const [reviewInterval, setReviewInterval] = useState<string>('');
  const [reminderEnabled, setReminderEnabled] = useState(false);
  const [firstReminderDays, setFirstReminderDays] = useState('28');
  const [secondReminderDays, setSecondReminderDays] = useState('14');
  const [saving, setSaving] = useState(false);

  // ── Publish-version fields ──
  const [publishForItemId, setPublishForItemId] = useState<string | null>(null);
  const [formSchema, setFormSchema] = useState('');
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [publishing, setPublishing] = useState(false);

  // ── Assign-now ──
  const [assigningId, setAssigningId] = useState<string | null>(null);

  async function loadItems() {
    try {
      setLoading(true);
      const data = await complianceApi.adminGetItems();
      setItems(data);
    } catch (err) {
      console.warn('Failed to load items', err);
      setError('Could not load compliance items.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (roles.isComplianceExec) loadItems();
  }, [roles.isComplianceExec]);

  function resetForm() {
    setCode('');
    setTitle('');
    setItemType('Attestation');
    setVideoId('');
    setIsActive(true);
    setGating(DEFAULT_GATING);
    setReviewInterval('');
    setReminderEnabled(false);
    setFirstReminderDays('28');
    setSecondReminderDays('14');
  }

  function startCreate() {
    resetForm();
    setEditingId(null);
    setMode('create');
    setError(null);
    setNotice(null);
  }

  function startEdit(item: ComplianceItemAdminListItem) {
    setEditingId(item.complianceItemId);
    setCode(item.code);
    setTitle(item.title);
    setItemType(item.itemType);
    setVideoId(item.synthesiaVideoId ?? '');
    setIsActive(item.isActive);
    setReviewInterval(item.reviewIntervalMonths != null ? String(item.reviewIntervalMonths) : '');
    setReminderEnabled(item.reminderEnabled ?? false);
    setFirstReminderDays(String(item.firstReminderDays ?? 28));
    setSecondReminderDays(String(item.secondReminderDays ?? 14));
    // The admin list doesn't carry gating steps; fetch detail to populate them.
    complianceApi
      .getItem(item.complianceItemId)
      .then((d) => {
        if (d.gatingSteps && d.gatingSteps.length > 0) setGating(d.gatingSteps);
        else setGating(DEFAULT_GATING);
      })
      .catch(() => setGating(DEFAULT_GATING));
    setMode('edit');
    setError(null);
    setNotice(null);
  }

  function toggleGating(stepName: string, enabled: boolean) {
    setGating((prev) =>
      prev.map((g) => (g.step === stepName ? { ...g, enabled } : g)),
    );
  }

  async function saveItem() {
    setError(null);
    if (!title.trim()) {
      setError('Title is required.');
      return;
    }
    if (mode === 'create' && !code.trim()) {
      setError('Code is required.');
      return;
    }
    setSaving(true);
    try {
      if (mode === 'create') {
        await complianceApi.adminCreateItem({
          code: code.trim(),
          title: title.trim(),
          itemType,
          synthesiaVideoId: videoId.trim() || null,
          gatingSteps: itemType === 'Attestation' ? gating : undefined,
        });
        setNotice('Item created.');
      } else if (editingId) {
        await complianceApi.adminUpdateItem(editingId, {
          title: title.trim(),
          isActive,
          synthesiaVideoId: videoId.trim() || null,
          gatingSteps: itemType === 'Attestation' ? gating : undefined,
          reviewIntervalMonths: itemType === 'Declaration' && reviewInterval.trim() ? parseInt(reviewInterval, 10) : null,
          reminderEnabled,
          firstReminderDays: parseInt(firstReminderDays, 10) || 28,
          secondReminderDays: parseInt(secondReminderDays, 10) || 14,
        });
        setNotice('Item updated.');
      }
      setMode('list');
      await loadItems();
    } catch (err) {
      const m = err instanceof Error ? err.message : 'Save failed.';
      setError(m);
    } finally {
      setSaving(false);
    }
  }

  async function deleteItem(item: ComplianceItemAdminListItem) {
    if (!window.confirm(`Delete "${item.title}"? This soft-deletes the item.`)) return;
    try {
      await complianceApi.adminDeleteItem(item.complianceItemId);
      setNotice('Item deleted.');
      await loadItems();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed.');
    }
  }

  async function startPublish(item: ComplianceItemAdminListItem) {
    setError(null);
    setNotice(null);
    setPdfFile(null);
    // Prefill the current schema BEFORE opening, so the builder mounts with it.
    let schema = '';
    try {
      const d = await complianceApi.getItem(item.complianceItemId);
      schema = d.formSchema ?? '';
    } catch {
      /* keep empty */
    }
    setFormSchema(schema);
    setPublishForItemId(item.complianceItemId);
  }

  async function publishVersion() {
    if (!publishForItemId) return;
    setError(null);

    if (formSchema.trim()) {
      try {
        JSON.parse(formSchema);
      } catch {
        setError('Form schema is not valid JSON.');
        return;
      }
    }

    setPublishing(true);
    try {
      await complianceApi.adminPublishVersion(
        publishForItemId,
        formSchema.trim(),
        pdfFile ?? undefined,
      );
      setNotice('New version published.');
      setPublishForItemId(null);
      await loadItems();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Publish failed.');
    } finally {
      setPublishing(false);
    }
  }

  async function assignNow(item: ComplianceItemAdminListItem) {
    if (
      !window.confirm(
        `Assign "${item.title}" to all active staff now? Anyone with an outstanding older version is superseded.`,
      )
    )
      return;
    setAssigningId(item.complianceItemId);
    setError(null);
    try {
      const result = await complianceApi.adminAssignNow(item.complianceItemId);
      setNotice(
        `Assigned to ${result.assignedCount} staff (${result.supersededCount} superseded).`,
      );
      await loadItems();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Assign failed.');
    } finally {
      setAssigningId(null);
    }
  }

  // ── Guards ──
  if (!rolesLoaded) {
    return (
      <div className={styles.page}>
        <div className={styles.loading}>Loading&hellip;</div>
      </div>
    );
  }
  if (!roles.isComplianceExec) {
    return (
      <div className={styles.page}>
        <header className={styles.header}>
          <h1 className={styles.title}>Not authorised</h1>
          <p className={styles.subtitle}>Content management is for Compliance team members only.</p>
        </header>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <button className={styles.backBtn} onClick={() => navigate('/compliance/dashboard')}>
          ← Dashboard
        </button>
        <h1 className={styles.title}>Manage compliance content</h1>
        <p className={styles.subtitle}>Create items, publish versions, and assign attestations.</p>
      </header>

      {error && <div className={styles.error}>{error}</div>}
      {notice && <div className={styles.notice}>{notice}</div>}

      {/* ── List ── */}
      {mode === 'list' && (
        <div className={styles.card}>
          <div className={styles.toolbar}>
            <button className={styles.openBtn} onClick={startCreate}>
              + New item
            </button>
          </div>

          {loading ? (
            <div className={styles.loading}>Loading&hellip;</div>
          ) : items.length === 0 ? (
            <div className={styles.emptyState}>No compliance items yet.</div>
          ) : (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Item</th>
                  <th>Type</th>
                  <th>Version</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.complianceItemId}>
                    <td>
                      <div className={styles.cellName}>{item.title}</div>
                      <div className={styles.cellSub}>{item.code}</div>
                    </td>
                    <td>{item.itemType}</td>
                    <td>
                      {item.currentVersionNumber != null
                        ? `v${item.currentVersionNumber} (${item.versionCount} total)`
                        : 'No version'}
                    </td>
                    <td>
                      <span
                        className={`${styles.statusBadge} ${
                          item.isActive ? styles.statusDone : styles.statusOutstanding
                        }`}
                      >
                        {item.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <div className={styles.rowActions}>
                        <button className={styles.linkBtn} onClick={() => startEdit(item)}>
                          Edit
                        </button>
                        <button className={styles.linkBtn} onClick={() => startPublish(item)}>
                          Publish version
                        </button>
                        {item.itemType === 'Attestation' && (
                          <button
                            className={styles.linkBtn}
                            onClick={() => assignNow(item)}
                            disabled={assigningId === item.complianceItemId}
                          >
                            {assigningId === item.complianceItemId ? 'Assigning…' : 'Assign now'}
                          </button>
                        )}
                        <button className={styles.linkBtnDanger} onClick={() => deleteItem(item)}>
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ── Create / Edit ── */}
      {(mode === 'create' || mode === 'edit') && (
        <div className={styles.card}>
          <h2 className={styles.sectionTitle}>
            {mode === 'create' ? 'New item' : 'Edit item'}
          </h2>

          <div className={styles.adminForm}>
            <div className={styles.adminField}>
              <label className={styles.adminLabel}>Code</label>
              <input
                className={styles.adminInput}
                value={code}
                disabled={mode === 'edit'}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder="e.g. COC"
              />
              {mode === 'edit' && <p className={styles.note}>Code cannot be changed after creation.</p>}
            </div>

            <div className={styles.adminField}>
              <label className={styles.adminLabel}>Title</label>
              <input
                className={styles.adminInput}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Code of Conduct Acknowledgement"
              />
            </div>

            <div className={styles.adminField}>
              <label className={styles.adminLabel}>Type</label>
              <select
                className={styles.adminInput}
                value={itemType}
                disabled={mode === 'edit'}
                onChange={(e) => setItemType(e.target.value as ComplianceItemType)}
              >
                <option value="Attestation">Attestation (mandatory, assigned)</option>
                <option value="Declaration">Declaration (ad-hoc)</option>
                <option value="Policy">Policy (view-only document)</option>
              </select>
              {mode === 'edit' && <p className={styles.note}>Type cannot be changed after creation.</p>}
            </div>

            {itemType === 'Attestation' && (
              <>
                <div className={styles.adminField}>
                  <label className={styles.adminLabel}>Synthesia video ID</label>
                  <input
                    className={styles.adminInput}
                    value={videoId}
                    onChange={(e) => setVideoId(e.target.value)}
                    placeholder="e.g. 283e35d0-2dc0-4242-9858-f1e6ce4c8d7f"
                  />
                </div>

                <div className={styles.adminField}>
                  <label className={styles.adminLabel}>Gating steps</label>
                  <div className={styles.gatingList}>
                    {gating.map((g) => (
                      <label key={g.step} className={styles.gatingRow}>
                        <input
                          type="checkbox"
                          checked={g.enabled}
                          disabled={g.step === 'Form'}
                          onChange={(e) => toggleGating(g.step, e.target.checked)}
                        />
                        <span>
                          {g.order}. {g.step}
                          {g.step === 'Form' && ' (always required)'}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              </>
            )}

            {itemType === 'Declaration' && mode === 'edit' && (
              <div className={styles.adminField}>
                <label className={styles.adminLabel}>Review interval (months)</label>
                <input
                  className={styles.adminInput}
                  type="number"
                  min="1"
                  value={reviewInterval}
                  onChange={(e) => setReviewInterval(e.target.value)}
                  placeholder="e.g. 12 for annual review (leave blank for no review cycle)"
                  style={{ maxWidth: 320 }}
                />
                <p className={styles.note}>
                  When set, employees who have submitted this declaration will be reminded to review it after this many months.
                </p>
              </div>
            )}

            {/* CR-8: Automated reminders — configurable per item */}
            {mode === 'edit' && itemType === 'Attestation' && (
              <div className={styles.adminField}>
                <label className={styles.adminLabel}>Automated reminders</label>
                <label className={styles.gatingRow}>
                  <input
                    type="checkbox"
                    checked={reminderEnabled}
                    onChange={(e) => setReminderEnabled(e.target.checked)}
                  />
                  <span>Enable automated email reminders for outstanding assignments</span>
                </label>
                {reminderEnabled && (
                  <div style={{ marginTop: 12, display: 'flex', gap: 24, flexWrap: 'wrap' }}>
                    <div>
                      <label className={styles.adminLabel} style={{ fontSize: 13 }}>First reminder after</label>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <input
                          className={styles.adminInput}
                          type="number"
                          min="1"
                          value={firstReminderDays}
                          onChange={(e) => setFirstReminderDays(e.target.value)}
                          style={{ width: 80 }}
                        />
                        <span style={{ fontSize: 13, color: '#666' }}>days</span>
                      </div>
                    </div>
                    <div>
                      <label className={styles.adminLabel} style={{ fontSize: 13 }}>Second reminder after</label>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <input
                          className={styles.adminInput}
                          type="number"
                          min="1"
                          value={secondReminderDays}
                          onChange={(e) => setSecondReminderDays(e.target.value)}
                          style={{ width: 80 }}
                        />
                        <span style={{ fontSize: 13, color: '#666' }}>more days</span>
                      </div>
                    </div>
                  </div>
                )}
                {reminderEnabled && (
                  <p className={styles.note} style={{ marginTop: 8 }}>
                    Line manager escalation is sent automatically on 15 July and 15 December for items still outstanding after both reminders.
                  </p>
                )}
              </div>
            )}

            {mode === 'edit' && (
              <div className={styles.adminField}>
                <label className={styles.gatingRow}>
                  <input
                    type="checkbox"
                    checked={isActive}
                    onChange={(e) => setIsActive(e.target.checked)}
                  />
                  <span>Active</span>
                </label>
              </div>
            )}

            <div className={styles.formActions}>
              <button
                className={styles.secondaryBtn}
                onClick={() => {
                  setMode('list');
                  setError(null);
                }}
              >
                Cancel
              </button>
              <button className={styles.openBtn} onClick={saveItem} disabled={saving}>
                {saving ? 'Saving…' : mode === 'create' ? 'Create item' : 'Save changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Publish version modal ── */}
      {publishForItemId && (
        <div className={styles.overlay} onClick={() => !publishing && setPublishForItemId(null)}>
          <div className={styles.dialog} onClick={(e) => e.stopPropagation()}>
            <div className={styles.dialogHeader}>
              <h2 className={styles.dialogTitle}>Publish new version</h2>
              <button
                className={styles.closeBtn}
                onClick={() => !publishing && setPublishForItemId(null)}
              >
                ✕
              </button>
            </div>
            <div className={styles.dialogBody}>
              <div className={styles.adminField}>
                <label className={styles.adminLabel}>Form fields</label>
                <FormFieldBuilder
                  key={publishForItemId}
                  initialSchema={formSchema}
                  onChange={setFormSchema}
                />
              </div>

              <div className={styles.adminField}>
                <label className={styles.adminLabel}>Document (PDF, optional)</label>
                <input
                  type="file"
                  accept="application/pdf"
                  onChange={(e) => setPdfFile(e.target.files?.[0] ?? null)}
                />
              </div>
            </div>
            <div className={styles.formActions}>
              <button
                className={styles.secondaryBtn}
                onClick={() => setPublishForItemId(null)}
                disabled={publishing}
              >
                Cancel
              </button>
              <button className={styles.openBtn} onClick={publishVersion} disabled={publishing}>
                {publishing ? 'Publishing…' : 'Publish version'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
